import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import styles from './BookingDrawer.module.css';
import { mentorshipService } from '../services/mentorshipService';

const HybridBookingDrawer = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    portfolioLink: '',
    topGoal: '',
    name: '',
    email: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  useEffect(() => {
    if (isOpen) {
      // Lock body scroll when drawer is open
      document.body.style.overflow = 'hidden';
      
      // Load Calendly widget script
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    } else {
      // Restore body scroll when drawer is closed
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    // Listen for Calendly events from the inline widget
    const handleCalendlyEvent = async (e) => {
      // Log Calendly events for monitoring
      if (e.data && e.data.event && e.data.event.startsWith('calendly.')) {
        console.log('Calendly event:', e.data.event);
      }
      
      // Handle event scheduling
      if (e.data.event && e.data.event === 'calendly.event_scheduled') {
        const eventData = e.data.payload;
        console.log('Event scheduled - proceeding to payment collection');
        
        // Calendly handles all scheduling - we just need to process payment
        setSelectedEvent({
          uri: eventData.event?.uri || 'calendly_scheduled_event',
          start_time: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Placeholder
          end_time: new Date(Date.now() + 24 * 60 * 60 * 1000 + 45 * 60 * 1000).toISOString(),
          name: 'Mentorship Session (Calendly Scheduled)',
          calendly_uri: eventData.event?.uri,
          invitee: eventData.invitee
        });
        
        // Auto-fill form with invitee data if available
        if (eventData.invitee) {
          setFormData(prev => ({
            ...prev,
            name: eventData.invitee.name || prev.name,
            email: eventData.invitee.email || prev.email
          }));
        }
        
        // Move to next step
        setCurrentStep(2);
        toast.success('Time scheduled! Please complete your information and payment.');
      }
      
      // Also listen for other Calendly events for monitoring
      if (e.data.event && e.data.event === 'calendly.event_type_viewed') {
        console.log('User viewing available times');
      }
      
      if (e.data.event && e.data.event === 'calendly.date_and_time_selected') {
        console.log('User selected a time in Calendly');
      }
    };

    window.addEventListener('message', handleCalendlyEvent);
    
    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [currentStep, selectedEvent]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 2 && (!formData.portfolioLink || !formData.topGoal || !formData.name || !formData.email)) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      
      if (!selectedEvent) {
        toast.error('No time slot selected. Please select a time first.');
        return;
      }
      
      if (!selectedEvent.start_time) {
        toast.error('Selected time is invalid. Please try selecting again.');
        return;
      }
      
      // Create a unique slot ID from the Calendly event
      const slotId = selectedEvent.uri || `calendly_${selectedEvent.start_time}`;
      
      const bookingData = {
        slotId: slotId,
        datetime: selectedEvent.start_time,
        portfolioLink: formData.portfolioLink,
        topGoal: formData.topGoal,
        customerInfo: {
          name: formData.name,
          email: formData.email,
          timezone: formData.timezone
        },
        calendlyEvent: selectedEvent // Include Calendly event data
      };

      const response = await mentorshipService.createBooking(bookingData);
      
      if (response.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.checkoutUrl;
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedEvent(null);
    setFormData({
      portfolioLink: '',
      topGoal: '',
      name: '',
      email: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    onClose();
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const drawerVariants = {
    hidden: { x: '100%' },
    visible: { x: 0 },
    exit: { x: '100%' }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  // Pre-fill Calendly URL with customer info
  const calendlyUrl = new URL('https://calendly.com/todd-toddwilliamson');
  if (formData.name) {
    calendlyUrl.searchParams.set('name', formData.name);
  }
  if (formData.email) {
    calendlyUrl.searchParams.set('email', formData.email);
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className={styles.overlay}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleClose}
            role="button"
            tabIndex={0}
            aria-label="Close booking drawer"
          />

          {/* Drawer */}
          <motion.div
            className={styles.drawer}
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-title"
          >
            <div className={styles.drawerHeader}>
              <h2 id="booking-title" className={styles.drawerTitle}>
                Book Your Mentorship Session
              </h2>
              <button
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close booking drawer"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            <div className={styles.drawerContent}>
              {/* Progress Indicator */}
              <div className={styles.progressContainer}>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill}
                    style={{ width: `${(currentStep / 3) * 100}%` }}
                  />
                </div>
                <div className={styles.stepLabels}>
                  <span className={currentStep >= 1 ? styles.stepActive : ''}>
                    1. Schedule
                  </span>
                  <span className={currentStep >= 2 ? styles.stepActive : ''}>
                    2. Your Info
                  </span>
                  <span className={currentStep >= 3 ? styles.stepActive : ''}>
                    3. Payment
                  </span>
                </div>
              </div>

              {/* Step Content */}
              <div className={styles.stepContent}>
                {currentStep === 1 && (
                  <div className={styles.stepContainer}>
                    <h3>Schedule Your Session with Todd</h3>
                    <p className={styles.stepDescription}>
                      Select a time that works for you. After scheduling, you'll provide your portfolio details and complete payment.
                    </p>
                    
                    <div style={{ height: '500px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
                      <div 
                        className="calendly-inline-widget" 
                        data-url={calendlyUrl.toString()}
                        style={{ minWidth: '320px', height: '100%' }}
                      />
                    </div>
                    
                    <div className={styles.calendlyNotice}>
                      <p><strong>Note:</strong> After selecting a time, you'll provide your portfolio info and pay $300 to confirm your session.</p>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className={styles.stepContainer}>
                    <h3>Tell Us About Your Goals</h3>
                    <p className={styles.stepDescription}>
                      Session scheduled for: <strong>{formatDateTime(selectedEvent?.start_time)}</strong>
                    </p>
                    
                    <form className={styles.intakeForm}>
                      <div className={styles.formGroup}>
                        <label htmlFor="name" className={styles.formLabel}>
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={styles.formInput}
                          required
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label htmlFor="email" className={styles.formLabel}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={styles.formInput}
                          required
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label htmlFor="portfolioLink" className={styles.formLabel}>
                          Portfolio Link *
                        </label>
                        <input
                          type="url"
                          id="portfolioLink"
                          name="portfolioLink"
                          value={formData.portfolioLink}
                          onChange={handleInputChange}
                          className={styles.formInput}
                          placeholder="https://yourportfolio.com"
                          required
                        />
                        <p className={styles.fieldHelp}>
                          Website, Instagram, or any online portfolio where Todd can review your work
                        </p>
                      </div>

                      <div className={styles.formGroup}>
                        <label htmlFor="topGoal" className={styles.formLabel}>
                          What's your top career goal? *
                        </label>
                        <textarea
                          id="topGoal"
                          name="topGoal"
                          value={formData.topGoal}
                          onChange={handleInputChange}
                          className={styles.formTextarea}
                          rows="4"
                          placeholder="e.g., Get gallery representation, improve my portfolio for art fairs, develop my artistic voice..."
                          required
                        />
                      </div>
                    </form>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className={styles.stepContainer}>
                    <h3>Confirm & Pay</h3>
                    <p className={styles.stepDescription}>
                      Complete your payment to confirm your mentorship session.
                    </p>
                    
                    <div className={styles.bookingSummary}>
                      <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Session Date & Time:</span>
                        <span className={styles.summaryValue}>
                          {formatDateTime(selectedEvent?.start_time)}
                        </span>
                      </div>
                      <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Duration:</span>
                        <span className={styles.summaryValue}>45 minutes</span>
                      </div>
                      <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Mentor:</span>
                        <span className={styles.summaryValue}>Todd Williamson</span>
                      </div>
                      <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Price:</span>
                        <span className={styles.summaryPrice}>$300</span>
                      </div>
                    </div>

                    <div className={styles.termsContainer}>
                      <p className={styles.termsText}>
                        <strong>Important:</strong> Your Calendly appointment is reserved but not confirmed until payment is complete. 
                        Sessions are non-refundable once paid. You may reschedule with at least 48 hours notice.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className={styles.actionButtons}>
                {currentStep > 1 && (
                  <button
                    className={styles.secondaryButton}
                    onClick={handlePrevStep}
                    disabled={loading}
                  >
                    Back
                  </button>
                )}
                
                {currentStep === 1 ? (
                  <div className={styles.calendlyHelp}>
                    <p>Select a time above to continue</p>
                  </div>
                ) : currentStep === 2 ? (
                  <button
                    className={styles.primaryButton}
                    onClick={handleNextStep}
                    disabled={loading || !formData.portfolioLink || !formData.topGoal || !formData.name || !formData.email}
                  >
                    Continue to Payment
                  </button>
                ) : (
                  <button
                    className={styles.primaryButton}
                    onClick={handlePayment}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Pay $300 & Confirm Session'}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HybridBookingDrawer; 